import React, { Component } from 'react';
import styled from 'styled-components';
import uuid from 'uuid/v4';

import ManagedComponent from '../components/managed-component';

import Section, { DisabledSection, SectionChild } from '../components/section';
import TextImageRow from './text-image-row';
import SmartView from '../components/smart-view';
import { ContentBlockBase as ContentBlock } from '../components/content-block';
import theme, { fitbitGreenText, isThemeiOS } from '../themes';

import iOSInstalledCheck from '../icons/ios-installed-check.svg';

import {
  FULL_VH,
  NavBackLabel,
  SectionContainer,
  SectionBody,
  SmartButton
} from '../components/Common';

const SelectButton = styled.button`
  display: flex;
  border: 0;
  padding: 0;
  background-color: white;
  width: 100%;
  justify-content: space-between;
  font-size: 17px;

  & .item-content {
    width: 100%;
  }
`;

const CheckedIcon = styled.div`
  margin-top: -8px;
  position: absolute;
  top: 50%;
  right: 0;

  svg {
    height: 17px;
  }
`;

const Row = styled.div`
  position: relative;
  width: 100%;
`;

const SmallModalRow = styled.div`
  display: flex;
  padding: 12px 0;
  position: relative;
  width: 100%;
`;

const Title = styled.div`
  font-size: 20px;
  margin-bottom: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RadioButton = styled.div`
  height: 22px;
  margin-top: -9px;
  position: absolute;
  top: 50%;
  width: 22px;
  > div {
    border: 2px solid #272727;
    border-radius: 100px;
    height: 18px;
    width: 18px;
    ${({ selected }) => selected && `
      border: 2px solid ${fitbitGreenText};
      > div {
        background: ${fitbitGreenText};
        border-radius: 100px;
        height: 10px;
        left: 0;
        margin: 6px;
        position: absolute;
        top: 0;
        width: 10px;
      }
    `};
  }
`;

const SmallModalRowText = styled.div`
  flex-grow: 1;
  padding-left: 36px;
`;

const ScrollableModal = styled.div`
  height: 100%;
  overflow: hidden;
`;

const ScrollableInner = styled.div`
  height: ${FULL_VH - theme.NavbarHeight}px;
  overflow-y: auto;
`;

const Options = styled.div`
  max-height: 50vh;
  overflow-y: auto;
`;

class Select extends Component {
  static _type = 'Select';
  _type = 'Select';

  static defaultProps = {
    selectViewTitle: 'Select Item'
  };

  id = `select-${uuid()}`;

  state = {
    smartView: false
  };

  get selected() {
    const { value } = this.props;
    let { selected } = this.props;

    if (typeof value !== 'undefined') {
      return new Set(value.selected);
    }

    if (typeof selected !== 'undefined') {
      if (!Array.isArray(selected)) {
        selected = [selected];
      }
      return new Set(selected);
    }

    return new Set();
  }

  handleChange = (selectedIndex) => {
    const { options, multiple, onSelection } = this.props;
    const selected = this.selected;

    if (!multiple) {
      selected.clear();
    }

    if (selected.has(selectedIndex)) {
      selected.delete(selectedIndex);
    } else {
      selected.add(selectedIndex);
    }

    const values = [];

    options.forEach((option, index) => {
      if (selected.has(index)) {
        values.push(option);
      }
    });

    if (typeof onSelection === 'function') {
      onSelection({
        values,
        selected: Array.from(selected)
      });
    }
  };

  onToggleView = () => {
    const { actAsButton } = this.props;
    const { smartView } = this.state;
    const selected = this.selected;

    if (actAsButton) {
      if (smartView) {
        selected.clear();
      }
    }
    this.setState({ smartView: !smartView, selected });
  }

  defaultRenderItem = (value: *) => <TextImageRow label={value.name} />;

  renderSmallModalView = () => () => {
    const { options, selectViewTitle, renderItem = this.defaultRenderItem } = this.props;
    const selected = this.selected;

    const actionView = !isThemeiOS() && (
      <SectionContainer>
        <SectionBody>
          <ContentBlock>
            <SmartButton onClick={this.onToggleView}>
              Done
            </SmartButton>
          </ContentBlock>
        </SectionBody>
      </SectionContainer>
    );

    return (
      <ScrollableModal>
        <Title>{selectViewTitle}</Title>
        <Options>
          {options.map((option, index) => (
            <DisabledSection>
              <SelectButton
                key={index}
                className="smart-select"
                onClick={() => this.handleChange(index)}
              >
                <SmallModalRow>
                  <RadioButton selected={selected.has(index)}>
                    <div>
                      {selected.has(index) && <div />}
                    </div>
                  </RadioButton>
                  <SmallModalRowText>{renderItem(option)}</SmallModalRowText>
                </SmallModalRow>
              </SelectButton>
            </DisabledSection>
          ))}
        </Options>
        {actionView}
      </ScrollableModal>
    );
  }

  renderSmartView = () => () => {
    const { options, renderItem = this.defaultRenderItem } = this.props;
    const selected = this.selected;
    return (
      <ScrollableModal>
        <ScrollableInner>
          <Section>
            {options.map((option, index) => (
              <SectionChild>
                <SelectButton
                  key={index}
                  className="smart-select"
                  onClick={() => this.handleChange(index)}
                >
                  <Row>
                    {renderItem(option)}
                    {selected.has(index) &&
                      <CheckedIcon
                        dangerouslySetInnerHTML={{ __html: iOSInstalledCheck }}
                      />
                    }
                  </Row>
                </SelectButton>
              </SectionChild>
            ))}
          </Section>
        </ScrollableInner>
      </ScrollableModal>
    );
  };

  render() {
    const {
      options,
      label,
      title = label,
      selectViewTitle,
      actAsButton,
      disabled
    } = this.props;
    const selected = this.selected;

    if (!options || !options.length) {
      return null;
    }

    const name = Array.from(selected).map((index) => options[index].name).join(', ');

    return (
      <SectionChild title={title}>
        <SmartView
          id={this.id}
          value={name}
          label={label}
          disabled={disabled}
          actAsButton={actAsButton}
          navBackLabel={<NavBackLabel>Done</NavBackLabel>}
          navTitle={selectViewTitle}
          showSmartView={this.state.smartView}
          small={true}
          onToggleView={this.onToggleView}
          renderSmartView={
            isThemeiOS() ?
              this.renderSmartView() :
              this.renderSmallModalView()
          }
        />
      </SectionChild>
    );
  }
}

export default class extends Component {
  parseValue(rawValue) {
    try {
      return JSON.parse(rawValue);
    } catch (err) {
      return [];
    }
  }

  render() {
    const {
      label,
      title,
      selectViewTitle,
      actAsButton,
      disabled,
      multiple,
      options,
      selected,
      renderItem,
      onSelection,
      settingsKey
    } = this.props;

    const selectProps = {
      label,
      title,
      selectViewTitle,
      actAsButton,
      disabled,
      multiple,
      options,
      selected,
      renderItem,
      onSelection
    };

    if (!settingsKey || actAsButton) {
      return (
        <Select {...selectProps} />
      );
    }

    return (
      <ManagedComponent settingsKey={settingsKey}>
        {({ value, onChange }) => (
          <Select
            {...selectProps}
            value={this.parseValue(value)}
            onSelection={(value) => {
              if (typeof onSelection === 'function') {
                onSelection(value);
              }
              onChange(JSON.stringify(value));
            }}
          />
        )}
      </ManagedComponent>
    );
  }
}



// WEBPACK FOOTER //
// ./src/fitbit-components/select.js